import { useQuery } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/features/auth/AuthProvider";
import type { EntitlementsResponse } from "@/types/menu";

const FREE_FALLBACK: EntitlementsResponse = {
  tier: "free",
  entitlements: { "Batch-Cooking Pro": false },
  source: "no-key",
  is_trial: false,
  trial_ends_at: null,
  free_generations_used: 0,
  free_generation_quota: 1,
};

/**
 * Resolve the user's premium tier against the backend, which talks to
 * RevenueCat. Anonymous users get the free fallback so the UI is usable
 * before sign-up.
 *
 * Backend gating is the source of truth: every premium mutation calls
 * RevenueCat server-side. This hook just drives the UI.
 */
export function useEntitlements(): {
  data: EntitlementsResponse;
  isLoading: boolean;
  /** The tier could not be fetched (server/network error) — `data` is then
   *  the last known value, or the free fallback when there is none. */
  isError: boolean;
  isPremium: boolean;
  isReadOnly: boolean;
  isTrial: boolean;
  trialDaysLeft: number | null;
  refetch: () => void;
} {
  const { session, configured } = useAuth();
  const enabled = configured && Boolean(session);

  const query = useQuery<EntitlementsResponse>({
    queryKey: ["entitlements", session?.user.id],
    queryFn: async () => {
      try {
        return await api<EntitlementsResponse>("/api/v1/me/entitlements");
      } catch (e) {
        // Only a definitive client-side answer (401/403/404…) means "free".
        // A 5xx or a network failure says nothing about the subscription:
        // rethrow so TanStack Query retries and keeps the last known tier
        // instead of caching "free" for a paying user (and opening the
        // paywall on them) for the whole staleTime.
        if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
          return FREE_FALLBACK;
        }
        throw e;
      }
    },
    enabled,
    staleTime: 60_000,
  });

  const data = query.data ?? FREE_FALLBACK;
  const isPremium = data.tier === "cahier" || data.tier === "trial";
  const isReadOnly = data.tier === "read_only";
  const isTrial = data.tier === "trial";

  let trialDaysLeft: number | null = null;
  if (isTrial && data.trial_ends_at) {
    const msLeft = new Date(data.trial_ends_at).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(msLeft / 86_400_000));
  }

  return {
    data,
    isLoading: enabled && query.isLoading,
    isError: enabled && query.isError,
    isPremium,
    isReadOnly,
    isTrial,
    trialDaysLeft,
    refetch: () => void query.refetch(),
  };
}
